import React, { memo, useState } from "react";

const NuclearButton = ({ onClick, disabled, children, level, currentNuclear }) => {
    const isActive = currentNuclear >= level;

    return (
        <button
            onClick={onClick}
            disabled={!isActive || disabled}
            className={`
        relative overflow-hidden group
        px-6 py-4 rounded-lg
        font-bold text-sm uppercase tracking-wider
        transition-all duration-300
        ${isActive
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-lg shadow-red-500/50 hover:shadow-red-500/70 hover:scale-105'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                }
      `}
        >
            {isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            )}
            <div className="relative z-10 flex items-center gap-2">
                {children}
            </div>
            {!isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                    <div
                        className="h-full bg-gradient-to-r from-yellow-500 to-red-500 transition-all duration-500"
                        style={{ width: `${(currentNuclear / level) * 100}%` }}
                    ></div>
                </div>
            )}
        </button>
    );
};

const Nucleaire = memo(({ gameState, triggerAction, onClose, onScoreIncrease }) => {
    const [confirmMode, setConfirmMode] = useState(null);
    const nuclear = gameState?.joueur?.nuclear || 0;

    const handleAction = (action) => {
        // En mode conférence, on peut soit confirmer ici, soit passer directement au ciblage
        // On va garder la validation dans la popin pour le côté drama du template
        setConfirmMode(action);
    };

    const confirmAction = () => {
        triggerAction(confirmMode);
        setConfirmMode(null);
    };

    const actions = [
        {
            id: 'alert',
            level: 10,
            icon: '🔔',
            title: 'Alerte Nucléaire',
            description: 'Mettre en alerte les systèmes de défense',
            color: 'from-yellow-600 to-orange-600'
        },
        {
            id: 'leak',
            level: 25,
            icon: '☢️',
            title: 'Fuite Contrôlée',
            description: 'Libération radioactive tactique',
            color: 'from-orange-600 to-red-600'
        },
        {
            id: 'strike',
            level: 50,
            icon: '🎯',
            title: 'Frappe Ciblée',
            description: 'Attaque nucléaire de précision',
            color: 'from-red-600 to-red-700'
        },
        {
            id: 'doomsday',
            level: 75,
            icon: '💥',
            title: 'Apocalypse',
            description: 'Destruction totale et définitive',
            color: 'from-red-700 to-black'
        }
    ];

    return (
        <div className="w-full h-full bg-slate-900 rounded-2xl overflow-hidden border border-red-500/30 shadow-2xl relative">

            {/* Animated background effect */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-transparent to-yellow-500 animate-pulse"></div>
            </div>

            {/* Scanlines effect */}
            <div className="absolute inset-0 pointer-events-none opacity-5">
                <div className="w-full h-full" style={{
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)'
                }}></div>
            </div>

            {/* Header */}
            <div className="relative z-10 p-6 border-b border-red-500/30 bg-black/40 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="absolute inset-0 bg-red-500 blur-xl opacity-50 animate-pulse"></div>
                            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center border-2 border-red-400 shadow-lg shadow-red-500/50">
                                <svg viewBox="0 0 512 512" className="w-10 h-10 text-white">
                                    <path fill="currentColor" d="M256.04,273.772c6.625,0,13.219-1.766,19.016-5.109c18.156-10.5,24.406-33.828,13.922-52.031c-6.781-11.75-19.438-19.031-33.016-19.031c-6.656,0-13.219,1.766-19.016,5.109c-18.172,10.516-24.438,33.828-13.922,52.031C229.805,266.475,242.461,273.772,256.04,273.772z" />
                                </svg>
                            </div>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-red-300 to-yellow-400 mb-1 leading-tight">
                                CENTRE DE COMMANDEMENT NUCLÉAIRE
                            </h1>
                            <p className="text-xs text-gray-400 font-mono">
                                PÉRIODE: {gameState?.year}-{gameState?.year + 2} | CLASSIFICATION: TOP SECRET
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-white font-bold transition-all duration-200 hover:scale-105"
                    >
                        ✕ FERMER
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 overflow-y-auto max-h-[calc(90vh-100px)] custom-scrollbar">

                {/* Leader Info */}
                <div
                    className={`mb-8 p-6 rounded-xl bg-gradient-to-r from-gray-900 to-gray-800 border border-red-500/20 ${onScoreIncrease ? 'cursor-pointer hover:border-red-500/50 hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-700 transition-all duration-200 select-none' : ''}`}
                    onClick={onScoreIncrease}
                    title={onScoreIncrease ? '+5 Niveau de Menace' : undefined}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1 font-mono">Commandant Suprême</p>
                            <h2 className="text-xl font-bold text-red-500 uppercase tracking-tighter">{gameState?.joueur?.leader || 'Unknown'}</h2>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1 font-mono">Niveau de Menace</p>
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-500">
                                    {nuclear}
                                </span>
                                <span className="text-xl text-gray-600">/100</span>
                            </div>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-4 h-2 bg-black rounded-full overflow-hidden border border-red-500/20">
                        <div
                            className="h-full bg-gradient-to-r from-yellow-500 via-red-500 to-red-700 transition-all duration-1000 relative"
                            style={{ width: `${nuclear}%` }}
                        >
                            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                        </div>
                    </div>
                </div>

                {/* Confirm Mode / Target Selection Hint */}
                {confirmMode && (
                    <div className="mb-8 p-6 rounded-xl bg-red-950/40 border-2 border-red-500 animate-fade-in shadow-[0_0_30px_rgba(239,68,68,0.2)]">
                        <h3 className="text-xl font-bold text-red-400 mb-2 flex items-center gap-2">
                            <span className="animate-pulse">⚠️</span>
                            CONFIRMATION REQUISE
                        </h3>
                        <p className="text-gray-300 mb-6">
                            Voulez-vous vraiment lancer l'opération : <strong className="text-red-500 uppercase">{actions.find(a => a.id === confirmMode)?.title}</strong> ?
                            {(confirmMode === 'strike' || confirmMode === 'doomsday') && (
                                <span className="block mt-2 text-sm text-red-400/80 italic font-mono">
                                    * Cette action nécessitera la désignation d'une cible géographique.
                                </span>
                            )}
                        </p>

                        <div className="flex gap-4">
                            <button
                                onClick={confirmAction}
                                className="flex-1 px-6 py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-red-600/40 uppercase tracking-widest"
                            >
                                ✓ INITIALISER LE PROTOCOLE
                            </button>
                            <button
                                onClick={() => setConfirmMode(null)}
                                className="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-gray-300 font-bold rounded-lg transition-all duration-200 uppercase tracking-widest"
                            >
                                ANNULER
                            </button>
                        </div>
                    </div>
                )}

                {/* Actions Grid */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity duration-300 ${confirmMode ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                    {actions.map((action) => {
                        const isActive = nuclear >= action.level;

                        return (
                            <div
                                key={action.id}
                                className={`
                    relative p-6 rounded-xl border-2 transition-all duration-300 group
                    ${isActive
                                        ? 'border-red-500/20 bg-slate-900 hover:border-red-500/50 hover:bg-slate-800 shadow-lg'
                                        : 'border-slate-800 bg-slate-900/50'
                                    }
                  `}
                            >
                                {/* Lock overlay */}
                                {!isActive && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl z-10 backdrop-blur-[1px]">
                                        <div className="text-center">
                                            <div className="text-3xl mb-2 grayscale">🔒</div>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Niveau {action.level}% requis</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-start gap-4 mb-4">
                                    <div className={`text-4xl ${isActive ? 'group-hover:scale-110 transition-transform duration-300' : 'grayscale opacity-50'}`}>
                                        {action.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`text-lg font-bold mb-1 ${isActive ? 'text-red-400' : 'text-gray-700'}`}>
                                            {action.title}
                                        </h3>
                                        <p className={`text-xs leading-relaxed ${isActive ? 'text-gray-400' : 'text-gray-700'}`}>
                                            {action.description}
                                        </p>
                                    </div>
                                </div>

                                {isActive && (
                                    <button
                                        onClick={() => handleAction(action.id)}
                                        className={`
                        w-full px-4 py-3 rounded-lg font-black uppercase text-xs tracking-widest
                        bg-gradient-to-r ${action.color} text-white
                        hover:brightness-125 transition-all duration-200
                        shadow-lg shadow-black/40
                      `}
                                    >
                                        SÉLECTIONNER
                                    </button>
                                )}

                                {!isActive && (
                                    <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden mt-4">
                                        <div
                                            className="h-full bg-gradient-to-r from-gray-700 to-red-900 transition-all duration-500"
                                            style={{ width: `${Math.min((nuclear / action.level) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Warning footer */}
                <div className="mt-8 p-4 rounded-lg bg-red-950/20 border border-red-900/20">
                    <p className="text-[10px] text-red-500/60 text-center font-mono uppercase tracking-[0.2em] animate-pulse">
                        ⚠️ DANGER: Utilisation soumise au protocole de défense globale
                    </p>
                </div>
            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { bg: rgba(0,0,0,0.2); }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(239,68,68,0.3); border-radius: 2px; }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
});

export default Nucleaire;
